import {
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

import { AppStore } from '../../store';
import { AppService, PostMessageService } from '../../services/app';
import { BookingStore } from '../../../pages/booking/store';
import { FlightsData } from '../../models/api/flight';
import { FareReselectComponent } from './fare-reselect/fare-reselect.component';

@Component({
  selector: 'app-reselect',
  templateUrl: './reselect.component.html',
  standalone: true,
  imports: [FareReselectComponent],
})
export class ReselectComponent implements OnInit, OnDestroy {
  private _subscriptions$: Subscription[] = [];
  private _appStore = inject(AppStore);
  private _bookingStore = inject(BookingStore);
  private _appService = inject(AppService);
  private _content = viewChild<TemplateRef<any>>('content');
  private _postMessageService = inject(PostMessageService);
  private _modalService = inject(NgbModal);

  readonly data = this._bookingStore.reselectFlight;
  readonly selecting = signal<FlightsData[]>([]);
  readonly isAllowReselect = computed(() => {
    const data = this.data();
    if (!data) return true;

    const selecting = this.selecting();
    if (selecting.length === 0) return false;

    const selectingFlight = selecting[0];
    if (!selectingFlight && data.selectedFlightGroup) {
      return false;
    }

    const selectingRoundTripFlight = selecting[1];
    if (!selectingRoundTripFlight && data.selectedRoundTripFlightGroup) {
      return false;
    }

    return true;
  });
  readonly isSoldOut = computed(() => {
    const data = this.data();
    return (
      (!data.selectedFlightGroup && !data.selectedRoundTripFlightGroup) ||
      (data.selectedFlightGroup &&
        !data.selectedRoundTripFlightGroup &&
        data.selectedFlightGroup.flights.length ===
          data.selectedFlights?.length) ||
      (data.selectedFlightGroup &&
        data.selectedRoundTripFlightGroup &&
        data.selectedFlightGroup.flights.length +
          data.selectedRoundTripFlightGroup.flights.length ===
          data.selectedFlights?.length)
    );
  });

  ngOnInit(): void {
    this._subscriptions$.push(
      this._appService.showAnnounce$.subscribe(() => {
        const reselectRef = this._appStore.reselectRef();
        if (reselectRef) {
          reselectRef.close();
        }
      }),

      this._appService.showReselect$.subscribe(() => {
        if (this.data()) {
          this.open();
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this._subscriptions$.forEach((s) => s?.unsubscribe());
    this._subscriptions$ = [];
  }

  open(): NgbModalRef {
    this._postMessageService.postMessageScrollIntoView();

    const modalRef = this._modalService.open(this._content(), {
      backdrop: 'static',
      size: 'md',
    });
    modalRef.result.then(
      (result) => {
        document.getElementsByTagName('body')[0].style.overflow = 'auto';
        setTimeout(() => {
          document.getElementsByTagName('body')[0].style.overflow = 'auto';
        }, 1000);
      },
      (reason) => {
        document.getElementsByTagName('body')[0].style.overflow = 'auto';
        setTimeout(() => {
          document.getElementsByTagName('body')[0].style.overflow = 'auto';
        }, 1000);
      },
    );

    this._appStore.updateReselectRef(modalRef);
    return modalRef;
  }

  checkDisabledFlight(flight: FlightsData): boolean {
    const selectedFlights = this.data.selectedFlights();
    if (selectedFlights) {
      const selectedFlight = selectedFlights.find(
        (f) =>
          f.flightValue === flight.flightValue &&
          f.fareClass === flight.fareClass,
      );
      if (selectedFlight) {
        return true;
      }
    }
    return false;
  }

  checkSelectingFlight(flight: FlightsData): boolean {
    const selectingFlights = this.selecting();
    if (selectingFlights) {
      const selectingFlight = selectingFlights.find(
        (f) =>
          f.flightValue === flight.flightValue &&
          f.fareClass === flight.fareClass,
      );
      if (selectingFlight) {
        return true;
      }
    }
    return false;
  }

  onSelectFlight(flight: FlightsData): void {
    this.selecting.update((state) => {
      const otherFlights = state.filter((f) => f.leg !== flight.leg);
      return [...otherFlights, flight];
    });
  }

  onCancel(modal: NgbModalRef): void {
    modal.close('CANCEL');
    this._appService.redirectToSearchSessionExpired();
  }

  onReselect(modal: NgbModalRef): void {
    modal.close('OK');
    this._appService.redirectToReselectFlight(this.selecting());
  }
}
